import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";

const animalCategories = {
    domestic: ["cow.png", "dog.png", "cat.png", "horse.png", "goat.png", "sheep.png", "pig.png"],
    farm: ["cow.png", "horse.png", "goat.png", "sheep.png", "pig.png", "chicken.png", "duck.png", "goose.png", "turkey.png", "donkey.png", "rabbit.png", "buffalo.png", "bee_image.png", "dog.png"],
    pet: ["dog.png", "cat.png", "rabbit.png", "parrot.png", "hamster.png", "goldfish.png", "canary.png"],
    water: ["fish_image.png", "turtle.png", "dolphin.png", "shark.png", "crab.png", "octopus.png", "jellyfish.png", "starfish.png", "seahorse.png", "pufferfish.png", "swordfish.png", "clownfish.png", "goldfish.png", "crocodile.png"],
    wild: ["lion.png", "tiger.png", "elephant.png", "rhino.png", "giraffe.png", "zebra.png", "snake.png", "cheetah.png", "leopard.png", "wolf.png", "fox.png", "bear.png", "gorilla.png"],
};


function shuffleArray(array: any[]) {
    for (let i = array.length - 1; i > 0; i--) {
        // Generate a random index
        const randomIndex = Math.floor(Math.random() * (i + 1));
        // Swap elements
        [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
    }
    return array;
}

// Question Type
type QuestionType = {
    text: string;
    correctImages: string[];
    allImages: string[];
};



// Dynamic Sizing
const getDynamicSizes = () => {
    const screenWidth = window.innerWidth;
    const imageSize = screenWidth > 1024 ? 90 : screenWidth > 600 ? 200 : 80;
    return { imageSize, minDistance: imageSize * 1.5 };
};

const ChooseAnimalType = () => {
    const [question, setQuestion] = useState<QuestionType | null>(null);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [key: string]: boolean }>({});
    const [sizes, setSizes] = useState(getDynamicSizes());
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [correctImage, setCorrectImage] = useState<any[]>();
    const [selectedImage, setSelectedImage] = useState('');
    const modal = useRef<HTMLIonModalElement>(null);

    // Generate a question
    const generateRandomQuestion = (remainingImages: number): QuestionType => {
        const categories = Object.keys(animalCategories) as (keyof typeof animalCategories)[];
        const randomCategory = categories[Math.floor(Math.random() * categories.length)];

        const correctPool = animalCategories[randomCategory];
        const correctImages = shuffleArray(correctPool).slice(0, 3);
        setCorrectImage(correctImages);

        const otherCategories = categories.filter(cat => cat !== randomCategory);
        const otherImagesPool = otherCategories.flatMap(cat => animalCategories[cat]);
        const uniqueArr = otherImagesPool.filter((value, index, self) => self.indexOf(value) === index).filter(item => !correctImages.includes(item));
        // Ensure enough incorrect images exist
        const incorrectImages = shuffleArray(uniqueArr).slice(0, remainingImages);
        const allImages = [...correctImages, ...incorrectImages].sort(() => 0.5 - Math.random());

        return {
            text: `Choose ${randomCategory} animals`,
            correctImages,
            allImages,
        };
    };

    // Memoized function to generate a new question
    const generateQuestion = useCallback(() => {
        if (!containerRef.current) return;
        //setShowImage(true);
        setTimeout(() => {
            const imageSize = sizes.imageSize;
            const container = containerRef.current?.getBoundingClientRect();
            if (!container) return;

            const columns = Math.floor(container.width / (imageSize + 20));
            const rows = Math.floor(container.height / (imageSize + 20));
            const totalImage = columns * rows;
            const remainingImages = totalImage > 3 ? totalImage - 3 : 0; // Prevent negative values
            setShowImage(false);
            setQuestion(generateRandomQuestion(remainingImages));
        }, 1000);

    }, [sizes]);

    // Generate a question only once on mount
    useEffect(() => {
        generateQuestion();
        document.title = "Choose the Correct Type of Animal | UKG Worksheet on Animal Classification"; // Set page title
    }, []);



    const handleImageClick = (image: string) => {
        setSelectedImage(image);
        setSelectedAnswers(prev => {
            const newState = { ...prev };
            if (image in newState) {
                delete newState[image];
            } else {
                newState[image] = question?.correctImages.includes(image) ?? false;
            }
            return newState;
        });
    };

    // Check answer and generate new question
    useEffect(() => {
        if (!question) return;

        const selectedCorrectCount = Object.values(selectedAnswers).filter(val => val === true).length;
        const totalCorrect = question.correctImages.length;
        const allSelected = Object.keys(selectedAnswers).length === totalCorrect;

        if (selectedCorrectCount === totalCorrect && allSelected) {
            //setShowImage(true);
            setTimeout(() => {
                setSelectedAnswers({});
                setQuestion(null)
                generateQuestion();
                //setShowImage(false);
                setSelectedImage('');

            }, 2000);
        }
    }, [selectedAnswers, question, generateQuestion]);

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG children identify and classify animals as wild, domestic, pet, or farm animals through a fun 'Choose the Correct Type' worksheet. Perfect for early learners!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/animals/choose-types" />
            </Helmet>
            {showImage ? (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} alt="Loading" />
                </div>
            ) : (
                <div className="contentPart additionWord chooseImage match-container">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Choose the Correct Type of Animal</h1>

                            <p>
                                Boost your child&apos;s general awareness with this engaging <strong>&apos;Choose the Correct Animal Type&apos;</strong> worksheet designed for UKG learners. Kids will identify whether an animal is a wild, domestic, pet, or farm animal based on simple pictures or names.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Classify animals based on habitat and behavior</li>
                                <li>Understand the difference between wild, domestic, and farm animals</li>
                                <li>Improve visual recognition and general awareness</li>
                            </ul>

                            <h2>🧠 What&apos;s Included?</h2>
                            <p>
                                The worksheet contains names and/or pictures of common animals such as <em>lion, cow, cat, tiger, hen, dog, zebra, elephant, goat, and horse</em>. Students choose whether each animal is:
                            </p>
                            <ul>
                                <li>🐅 Wild Animal</li>
                                <li>🏠 Domestic Animal</li>
                                <li>🐕 Pet Animal</li>
                                <li>🚜 Farm Animal</li>
                            </ul>
                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Sense Organs</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-home"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My Home</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-school"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My School</IonLabel>
                                </IonItem>
                            </div>

                            <h2>👩‍🏫 Teaching Tips</h2>
                            <ul>
                                <li>Introduce the concept with real-life photos or toys</li>
                                <li>Tell short stories about each animal type</li>
                                <li>Use this worksheet as a group activity in class</li>
                            </ul>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Choose the Correct Type of Animal</h1>

                        <p>
                            Boost your child&apos;s general awareness with this engaging <strong>&apos;Choose the Correct Animal Type&apos;</strong> worksheet designed for UKG learners. Kids will identify whether an animal is a wild, domestic, pet, or farm animal based on simple pictures or names.
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Classify animals based on habitat and behavior</li>
                            <li>Understand the difference between wild, domestic, and farm animals</li>
                            <li>Improve visual recognition and general awareness</li>
                        </ul>

                        <h2>🧠 What&apos;s Included?</h2>
                        <p>
                            The worksheet contains names and/or pictures of common animals such as <em>lion, cow, cat, tiger, hen, dog, zebra, elephant, goat, and horse</em>. Students choose whether each animal is:
                        </p>
                        <ul>
                            <li>🐅 Wild Animal</li>
                            <li>🏠 Domestic Animal</li>
                            <li>🐕 Pet Animal</li>
                            <li>🚜 Farm Animal</li>
                        </ul>
                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Sense Organs</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/general-awareness/my-home"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>My Home</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/general-awareness/my-school"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>My School</IonLabel>
                            </IonItem>
                        </div>

                        <h2>👩‍🏫 Teaching Tips</h2>
                        <ul>
                            <li>Introduce the concept with real-life photos or toys</li>
                            <li>Tell short stories about each animal type</li>
                            <li>Use this worksheet as a group activity in class</li>
                        </ul>
                    </article>
                    <h6 style={{ textAlign: "center" }}>{question?.text}</h6>
                    <div ref={containerRef} className="chooseImageContainer" style={{ position: "relative", width: "100%", height: "100%", justifyContent: "center", alignItems: "center", overflow: "hidden", display: "ruby" }}>
                        {question?.allImages.map((img, index) => (
                            <div key={img + index} >
                                <img src={`${process.env.REACT_APP_API_URL}${img}`} alt="option" onClick={() => handleImageClick(img)} style={{
                                    width: `${sizes.imageSize}px`,
                                    height: `${sizes.imageSize}px`,
                                    cursor: "pointer",
                                    border: `5px solid ${img in selectedAnswers ? (selectedAnswers[img] === true ? "green" : "red") : "transparent"}`,
                                    transition: "transform 0.2s ease-in-out",
                                }} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default ChooseAnimalType;
